/**
 * The Captains screen's controller and modal overlay (M6 plan P32), on the Fleet screen's pattern (`fleetHost.tsx`).
 *
 * - **Controller** (`createCaptainsController`, Node-tested): holds the `CaptainsScreenState`, reads the captain
 *   view (the strategic client's `captainView(null)`: hired captains and the memorial) and the fleet view on
 *   `refresh()`, runs `reduceCaptains` for every action and carries out its effects through the `fleet` service:
 *   `assignCaptain` (ship or none; never a station here) and `fireCaptain`, each followed by a refresh.
 * - **Section** (`createCaptainsSection`, dashboard W3 card M2): the body of Fleet › Captains. The dashboard shell
 *   owns the frame, Close, the hints, the modal and the keys; its focus inputs arrive through the `DashNavPort`:
 *   arrows move (↑ from the first row reaches the bar, replacing the old in-panel Tab; ← cancels the picker), Enter
 *   or Space presses. The pad goes through `input` (the owner's `stepPadNav`).
 *
 * UI events only.
 */
import { useEffect, type ReactElement } from 'react'
import { logger } from '../../core/logger'
import type { FleetService } from '../../fleet-link/service'
import type { CaptainView } from '../../strategic/types'
import type { DashSectionComponent, DashSectionProps } from '../dashboard/registry'
import type { NavInput } from '../strategic/mount'
import { Captains } from './Captains'
import { toNavInput, useLive, useNavInput } from './sectionHost'
import {
  assignedText,
  captainRows,
  createCaptainsState,
  reduceCaptains,
  type CaptainNames,
  type CaptainsAction,
  type CaptainsConfig,
  type CaptainsEffect,
  type CaptainsScreenState,
} from './models/captains'
import { memorialRows } from './models/memorial'
import { errorText } from './models/outcome'

/** What the Captains screen reads and sends. */
export interface CaptainsScreenService extends Pick<FleetService, 'fleetView' | 'run'> {
  /** The hired captains and the memorial (the strategic client's `captainView(null)`). */
  captainView(): Promise<CaptainView>
}

export interface CaptainsControllerDeps {
  service: CaptainsScreenService
  names: CaptainNames
  config: CaptainsConfig
  onClose?: () => void
}

export interface CaptainsController {
  readonly state: CaptainsScreenState
  readonly loading: boolean
  dispatch(a: CaptainsAction): void
  /** Re-reads the captains and the fleet; resolves when the new rows are in. */
  refresh(): Promise<void>
  /** Resolves when the last command (and its refresh) finished; for tests. */
  settled(): Promise<void>
  onChange(fn: () => void): () => void
}

/** A refusal in words; `busy` on a captain means recovering or on strike. */
function refusal(code: string): string {
  return code === 'busy' ? 'A recovering or striking captain cannot take a new ship.' : errorText(code)
}

export function createCaptainsController(deps: CaptainsControllerDeps): CaptainsController {
  let state = createCaptainsState()
  let loading = true
  let pending: Promise<void> = Promise.resolve()
  const listeners = new Set<() => void>()

  function changed(): void {
    for (const fn of [...listeners]) fn()
  }

  function step(a: CaptainsAction): void {
    const r = reduceCaptains(state, a)
    state = r.state
    changed()
    if (r.effect !== null) run(r.effect)
  }

  async function command(e: Exclude<CaptainsEffect, { kind: 'close' }>): Promise<void> {
    const ships = state.ships
    const res =
      e.kind === 'fire'
        ? await deps.service.run({ type: 'fireCaptain', captainId: e.captainId })
        : await deps.service.run({ type: 'assignCaptain', captainId: e.captainId, shipId: e.shipId, stationId: null })
    if (res.ok) {
      step({ type: 'notice', text: e.kind === 'fire' ? `${e.name} has left your service.` : assignedText(e.name, e.shipId, ships) })
    } else {
      logger.warn('Captains screen: command refused', { kind: e.kind, captain: e.captainId, error: res.error })
      step({ type: 'notice', text: refusal(res.error) })
    }
    await controller.refresh()
  }

  function run(e: CaptainsEffect): void {
    if (e.kind === 'close') {
      deps.onClose?.()
      return
    }
    pending = command(e).catch((err: unknown) => {
      logger.warn('Captains screen: command failed', err)
      step({ type: 'notice', text: 'The fleet link did not answer.' })
    })
  }

  const controller: CaptainsController = {
    get state(): CaptainsScreenState {
      return state
    },
    get loading(): boolean {
      return loading
    },
    dispatch: step,
    async refresh(): Promise<void> {
      try {
        const [view, fleet] = await Promise.all([deps.service.captainView(), deps.service.fleetView()])
        loading = false
        step({
          type: 'data',
          rows: captainRows(view, fleet, deps.names, deps.config),
          memorial: memorialRows(view.memorial, deps.names, deps.config),
          ships: fleet.ships,
          hiredCap: view.hiredCap,
          soleTrader: fleet.soleTrader,
        })
      } catch (err: unknown) {
        loading = false
        logger.warn('Captains screen: could not read the captains', err)
        step({ type: 'notice', text: 'Could not read your captains.' })
      }
    },
    settled: () => pending,
    onChange(fn: () => void): () => void {
      listeners.add(fn)
      return () => {
        listeners.delete(fn)
      }
    },
  }
  return controller
}

// ---------------------------------------------------------------------------------------------
// The dashboard section (Fleet › Captains; W3 card M2)
// ---------------------------------------------------------------------------------------------

export interface CaptainsSectionOptions {
  service: CaptainsScreenService
  names: CaptainNames
  config: CaptainsConfig
}

export interface CaptainsSectionHost {
  readonly controller: CaptainsController
  /** The section body (`registerDashSection`); the shell owns the frame, Close, hints and keys. */
  readonly component: DashSectionComponent
  readonly shown: boolean
  /** A pad input while shown (the owner's `stepPadNav`). */
  input(i: NavInput): void
}

export function createCaptainsSection(opts: CaptainsSectionOptions): CaptainsSectionHost {
  let shown = 0
  const controller = createCaptainsController({ service: opts.service, names: opts.names, config: opts.config })
  const subscribe = (fn: () => void): (() => void) => controller.onChange(fn)
  const refresh = (): Promise<void> => controller.refresh()

  function CaptainsSection({ nav }: DashSectionProps): ReactElement {
    useLive(subscribe, refresh, opts.config.refreshS * 1000)
    // ↑ from the first row reaches the Captains/Memorial bar and ↓ comes back (the old in-panel Tab); ←/→ pick
    // Reassign or Fire, or cancel the picker.
    useNavInput(nav, (i) => {
      const n = toNavInput(i)
      if (n === null) return false
      controller.dispatch({ type: 'nav', input: n })
      return true
    })
    useEffect(() => {
      shown++
      return (): void => {
        shown--
      }
    }, [])
    return <Captains state={controller.state} config={opts.config} dispatch={controller.dispatch} loading={controller.loading} />
  }

  return {
    controller,
    component: CaptainsSection,
    get shown(): boolean {
      return shown > 0
    },
    input(i: NavInput): void {
      if (shown > 0) controller.dispatch({ type: 'nav', input: i })
    },
  }
}
